import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthProvider';
import Topbar from '@/components/platform/Topbar';
import { RequireApprovedMember } from '@/components/platform/Guards';
import { fetchMyInvitation } from '@/lib/weddingApi';
import { inviteShareUrl } from '@/lib/share';
import {
  STATUS_LABEL,
  formatKoreanDate,
  type WeddingInvitation,
} from '@/types/wedding';
import ResponsesPanel from './components/ResponsesPanel';

const FALLBACK_COVER =
  'https://readdy.ai/api/search-image?query=elegant%20minimal%20wedding%20stationery%20flat%20lay%20on%20a%20dark%20walnut%20table%2C%20warm%20cinematic%20light%2C%20cream%20roses%20and%20gold%20accents%2C%20editorial%20photography%2C%20muted%20warm%20brown%20tones&width=1200&height=675&seq=pf-dash-cover&orientation=landscape';

function DashboardBody() {
  const { member } = useAuth();
  const [invitation, setInvitation] = useState<WeddingInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!member) return;
    setLoading(true);
    setError('');
    try {
      setInvitation(await fetchMyInvitation(member.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : '청첩장 정보를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [member]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!member) return null;

  const cover = invitation?.data.photos[0] || FALLBACK_COVER;
  const coupleNames = invitation
    ? `${invitation.data.groom.name || '신랑'} · ${invitation.data.bride.name || '신부'}`
    : '';
  const dateText = invitation ? formatKoreanDate(invitation.data.date) : '';

  const copyLink = async () => {
    if (!invitation) return;
    try {
      await navigator.clipboard.writeText(inviteShareUrl(invitation.slug));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('링크를 복사하지 못했어요.');
    }
  };

  return (
    <div className="pf-page">
      <Topbar />
      <main className="pf-main">
        <div className="pf-narrow">
          <div className="pf-page-head">
            <h1>{member.name ? `${member.name}님, 안녕하세요` : '안녕하세요'}</h1>
            <p>내 모바일 청첩장을 만들고 관리할 수 있어요.</p>
          </div>

          <div className="pf-grid-3" style={{ marginBottom: 24 }}>
            <div className="pf-stat">
              <span className="lbl">회원 상태</span>
              <span className={`pf-badge ${member.status}`} style={{ alignSelf: 'flex-start' }}>
                <i className="ri-checkbox-circle-line" aria-hidden="true" />
                {STATUS_LABEL[member.status]}
              </span>
            </div>
            <div className="pf-stat">
              <span className="num">{invitation ? 1 : 0}</span>
              <span className="lbl">내 청첩장</span>
            </div>
            <div className="pf-stat">
              <span className="num">5</span>
              <span className="lbl">사용 가능한 디자인</span>
            </div>
          </div>

          <div className="pf-card pf-invite-card">
            <div className="pf-card-head">
              <h2>내 모바일 청첩장</h2>
              {invitation ? (
                <span className={`pf-badge ${invitation.published ? 'approved' : 'pending'}`}>
                  <i
                    className={invitation.published ? 'ri-global-line' : 'ri-eye-off-line'}
                    aria-hidden="true"
                  />
                  {invitation.published ? '공개 중' : '비공개'}
                </span>
              ) : (
                <span className="pf-badge admin">
                  <i className="ri-sparkling-2-line" aria-hidden="true" />
                  신규
                </span>
              )}
            </div>

            {error ? (
              <div className="pf-alert err" role="alert">
                <i className="ri-error-warning-line" aria-hidden="true" />
                <span>{error}</span>
              </div>
            ) : null}

            <div className="pf-invite-preview">
              <img src={cover} alt="청첩장 미리보기" />
              <span className="tag">
                {loading
                  ? '불러오는 중…'
                  : invitation
                    ? [coupleNames, dateText].filter(Boolean).join(' · ')
                    : '아직 만들지 않았어요'}
              </span>
            </div>

            <p className="pf-muted">
              {invitation
                ? '만든 청첩장을 수정하거나, 완성된 링크를 하객에게 공유할 수 있어요.'
                : '신랑·신부 정보와 예식 정보, 인사말, 사진을 입력하고 마음에 드는 디자인을 고르면 청첩장이 완성돼요. 하객에게는 완성된 링크를 공유할 수 있어요.'}
            </p>

            {invitation ? (
              <div className="ed-field" style={{ marginBottom: 4 }}>
                <span className="ed-label">공유 링크</span>
                <input
                  className="ed-input"
                  readOnly
                  value={inviteShareUrl(invitation.slug)}
                  onFocus={(e) => e.target.select()}
                />
              </div>
            ) : null}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Link className="pf-btn primary" to="/editor">
                <i className={invitation ? 'ri-edit-2-line' : 'ri-add-line'} aria-hidden="true" />
                {invitation ? '청첩장 수정하기' : '청첩장 만들기'}
              </Link>
              {invitation ? (
                <>
                  <button type="button" className="pf-btn" onClick={() => void copyLink()}>
                    <i className={copied ? 'ri-check-line' : 'ri-link'} aria-hidden="true" />
                    {copied ? '복사됨' : '링크 복사'}
                  </button>
                  <Link
                    className="pf-btn ghost"
                    to={`/invite/${invitation.slug}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="ri-external-link-line" aria-hidden="true" />
                    청첩장 보기
                  </Link>
                </>
              ) : null}
            </div>
          </div>

          {invitation ? <ResponsesPanel invitationId={invitation.id} /> : null}
        </div>
      </main>
    </div>
  );
}

export default function MyPage() {
  return (
    <RequireApprovedMember>
      <DashboardBody />
    </RequireApprovedMember>
  );
}